import React, { useState, useEffect, useCallback } from 'react';
import { client } from '../utils/api';

const CONTROLLER = 'eventRegistrationController.php';
const EVENTS_CONTROLLER = 'eventsController.php';
const USERS_CONTROLLER = 'usersController.php';
const BASE_URL = process.env.REACT_APP_DB_BASE_URL || 'http://localhost/api';

export default function EventRegistrationsManager({ onSendEmail = () => {} }) {
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [registrations, setRegistrations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [emailOptions, setEmailOptions] = useState({ registered: true, unpaid: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadEvents = useCallback(async () => {
    try {
      const res = await client.get(EVENTS_CONTROLLER);
      setEvents(res.events || []);
      if (!selectedEventId && (res.events || []).length) {
        setSelectedEventId(String(res.events[0].id));
      }
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  }, [selectedEventId]);

  const loadUsers = useCallback(async () => {
    try {
      const res = await client.get(USERS_CONTROLLER);
      setUsers(res.users || []);
    } catch (err) {
      setError((previous) => previous || 'Failed to fetch users: ' + err.message);
    }
  }, []);

  const loadRegistrationsForEvent = useCallback(async (eventId) => {
    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${eventId}`, {
        method: 'GET', // or POST depending on your endpoint
        credentials: 'include' // MANDATORY to attach the PHPSESSID cookie
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setRegistrations(result.registrations || []);
    } catch (err) {
      setError('Failed to fetch registrations: ' + err.message);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadUsers();
  }, [loadEvents, loadUsers]);

  useEffect(() => {
    if (!selectedEventId) {
      setRegistrations([]);
      return;
    }

    loadRegistrationsForEvent(selectedEventId);
  }, [selectedEventId, loadRegistrationsForEvent]);

  const selectedEvent = events.find((event) => String(event.id) === String(selectedEventId)) || null;

  const availableUsersForEvent = users.filter((user) => {
    const isAlreadyRegistered = registrations.some(
      (registration) => Number(registration.user_id) === Number(user.id)
    );

    if (isAlreadyRegistered) {
      return false;
    }

    const query = userSearchTerm.trim().toLowerCase();
    if (!query) {
      return false;
    }

    const searchableText = `${user.fullName || ''} ${user.email || ''}`.toLowerCase();
    return searchableText.includes(query);
  });

  const handleSendEmail = () => {
    if (!selectedEvent) {
      setError('Choose an event before sending email.');
      return;
    }

    const statusMatches = (status) => {
      const normalized = String(status || '').trim().toLowerCase();
      const registeredStatuses = ['registered', 'registered/paid', 'paid', 'confirmed', 'attending'];

      if (emailOptions.registered && registeredStatuses.includes(normalized)) {
        return true;
      }

      if (emailOptions.unpaid && normalized === 'unpaid') {
        return true;
      }

      return false;
    };

    const recipients = Array.from(
      new Set(
        registrations
          .filter((registration) => statusMatches(registration.registration_status))
          .map((registration) => registration.email)
          .filter(Boolean)
      )
    );

    if (recipients.length === 0) {
      setError('No matching registration emails are available for the selected options.');
      return;
    }

    setError('');
    onSendEmail({
      recipients,
      subject: `RE ${selectedEvent.title}`,
    });
  };

  const handleAddUser = async (userIdToAdd = selectedUserId) => {
    const targetUserId = Number(userIdToAdd);
    if (!selectedEventId || !targetUserId) {
      setError('Choose an event and a user before adding a registration.');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/${CONTROLLER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: Number(selectedEventId), userId: targetUserId }),
      });

      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      setSelectedUserId('');
      setUserSearchTerm('');
      setSuccess('User added to the event.');
      await loadRegistrationsForEvent(selectedEventId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteRegistration = async (userId) => {
    if (!selectedEventId) return;

    if (!window.confirm('Remove this user from the event?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      const response = await fetch(`${BASE_URL}/${CONTROLLER}?eventId=${selectedEventId}&userId=${userId}`, {
        method: 'DELETE',
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }
      setSuccess('User removed from the event.');
      await loadRegistrationsForEvent(selectedEventId);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-view-wrapper">
      <div className="admin-view-header">
        <h2 className="admin-title">Event Registrations</h2>
        {error && <div className="admin-error">{error}</div>}
        {success && <div className="admin-success">{success}</div>}
      </div>

      <div className="admin-registration-layout">
        <aside className="admin-event-list-panel">
          <div className="panel-header">
            <h3>Events</h3>
          </div>

          {events.length === 0 ? (
            <div className="no-records">No events found.</div>
          ) : (
            events.map((event) => (
              <button
                key={event.id}
                type="button"
                className={`event-select-card ${String(event.id) === String(selectedEventId) ? 'active' : ''}`}
                onClick={() => setSelectedEventId(String(event.id))}
              >
                <strong>{event.title}</strong>
                <span>{event.location || 'Location not set'}</span>
                <small>{event.startTime ? new Date(event.startTime).toLocaleString() : 'No date'}</small>
              </button>
            ))
          )}
        </aside>

        <section className="admin-registration-detail-panel">
          {!selectedEvent ? (
            <div className="no-records">Select an event to manage registrations.</div>
          ) : (
            <>
              <div className="panel-header">
                <h3>{selectedEvent.title}</h3>
              </div>

              <div className="event-registration-meta">
                <span>{selectedEvent.location || 'No location'}</span>
                <span>{selectedEvent.startTime ? new Date(selectedEvent.startTime).toLocaleString() : 'No date'}</span>
              </div>

              <div className="admin-form-container registration-add-form">
                <div className="form-group">
                  <label>Add user to this event</label>
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={userSearchTerm}
                    onChange={(e) => {
                      setUserSearchTerm(e.target.value);
                      setSelectedUserId('');
                    }}
                  />
                </div>

                <div className="registration-email-actions">
                  <div className="registration-email-toggle-group">
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        checked={emailOptions.registered}
                        onChange={() =>
                          setEmailOptions((previous) => ({
                            ...previous,
                            registered: !previous.registered,
                          }))
                        }
                      />
                      Registered
                    </label>
                    <label className="form-checkbox-label">
                      <input
                        type="checkbox"
                        checked={emailOptions.unpaid}
                        onChange={() =>
                          setEmailOptions((previous) => ({
                            ...previous,
                            unpaid: !previous.unpaid,
                          }))
                        }
                      />
                      Unpaid
                    </label>
                  </div>

                  <button type="button" className="btn btn-submit-professionals" onClick={handleSendEmail}>
                    Send Email
                  </button>
                </div>

                {userSearchTerm.trim() && (
                  <div className="user-search-results">
                    {availableUsersForEvent.length === 0 ? (
                      <div className="no-records">No matching users available.</div>
                    ) : (
                      availableUsersForEvent.slice(0, 12).map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          className="user-search-result"
                          onClick={() => handleAddUser(user.id)}
                        >
                          <strong>{user.fullName || 'Unknown user'}</strong>
                          <span>{user.email}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <div className="admin-table-section">
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr className="table-header-row">
                        <th className="table-header-cell">User</th>
                        <th className="table-header-cell">Email</th>
                        <th className="table-header-cell">Status</th>
                        <th className="table-header-cell">Registered</th>
                        <th className="table-header-cell table-header-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registrations.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="table-body-cell">No users registered for this event.</td>
                        </tr>
                      ) : (
                        registrations.map((registration) => (
                          <tr key={registration.id} className="table-body-row">
                            <td className="table-body-cell">{registration.full_name || 'Unknown user'}</td>
                            <td className="table-body-cell">{registration.email || '—'}</td>
                            <td className="table-body-cell">{registration.registration_status || 'unpaid'}</td>
                            <td className="table-body-cell">{registration.registered_at ? new Date(registration.registered_at).toLocaleString() : '—'}</td>
                            <td className="table-body-cell table-body-actions">
                              <button
                                type="button"
                                className="btn-action-delete"
                                onClick={() => handleDeleteRegistration(registration.user_id)}
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
